import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { DATE_TIME_DASH, DATE_FORMAT_MONTH_DAY, DATE_FORMAT_MONTH_DAY_YEAR } from '../../utils/dates';
import FeedFloLabChart from '../../molecules/FeedFloLabsChart';

const timeBinSizeInSeconds = 5 * 60;

const DEVICE_TRANSATION_GQL = gql`
  query DeviceTransactionQuery($deviceId: uuid!, $from: bigint!, $to: bigint!) {
    device(where: { id: { _eq: $deviceId } }) {
      serial
    }
    device_transaction(where: { device_id: { _eq: $deviceId }, occured_at: { _gte: $from, _lte: $to } }) {
      occured_at
      type
      data
    }
  }
`;

export default function TransactionChart({ deviceId, to, from }) {
  const [chartData, setChartData] = useState([]);
  const { error, data } = useQuery(DEVICE_TRANSATION_GQL, {
    variables: {
      deviceId,
      from: from.unix(),
      to: to.unix(),
    },
  });

  // if (loading) return <span>Loading...</span>;
  if (error) return <span>Error {JSON.stringify(error)}</span>;

  useEffect(() => {
    const transactions = data?.device_transaction;
    if (!transactions) return;

    const min = from.unix();
    const max = to.unix();
    const binCount = Math.ceil((max - min) / timeBinSizeInSeconds);

    // Build x-axis labels once — shared across all series (Plotly reads, never mutates)
    const xLabels = [];
    for (let i = 0; i < binCount; i++) {
      xLabels.push(dayjs.tz(1000 * (min + i * timeBinSizeInSeconds)).format(DATE_TIME_DASH));
    }

    // Accumulate per-series y counts using fill() instead of JSON deep-clone
    const yMap = {};
    transactions.forEach((t) => {
      if (!yMap[t.data]) {
        yMap[t.data] = new Array(binCount).fill(0);
      }
      const bin = Math.floor((t.occured_at - min) / timeBinSizeInSeconds);
      if (bin >= 0 && bin < binCount) {
        yMap[t.data][bin] += 1;
      }
    });

    const series = Object.entries(yMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, y]) => ({ type: 'scatter', name, x: xLabels, y }));

    setChartData(series);
  }, [data]);

  return (
    <FeedFloLabChart
      data={chartData}
      layout={{
        title: {
          text: `<b>Http Requests</b> ${data?.device?.[0]?.serial}<br>[sum over ${
            timeBinSizeInSeconds / 60
          }min]<br>${from.format(DATE_FORMAT_MONTH_DAY)} - ${to.format(DATE_FORMAT_MONTH_DAY_YEAR)}`,
        },
      }}
    />
  );
}

TransactionChart.propTypes = {
  deviceId: PropTypes.string,
  to: PropTypes.object,
  from: PropTypes.object,
};
